/**
 * 空间设置：展示/编辑空间名称、代码；Logo 上传；是否认证只读。
 */
import { useState, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@hs/shared/stores/hooks';
import { updateUserInfo } from '@hs/shared/stores/slices/authSlice';
import { trpc } from '@/lib/trpc';
import { Avatar, Button, Card, Input, Label, Separator, TextField, Description } from '@heroui/react';
import { AvatarUploadDialog } from '@/components/AvatarUploadDialog';

function getSpaceInitial(name: string | null | undefined): string {
  if (!name || name.trim() === '') return 'S';
  return name.charAt(0);
}

export function SpaceSettingsPanel() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const spaceId = user?.spaces?.[0]?.id;
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [logoUploadOpen, setLogoUploadOpen] = useState(false);

  const { data: space, isLoading } = trpc.spaces.get.useQuery(
    { id: spaceId! },
    { enabled: isHydrated && isAuthenticated && !!spaceId }
  );

  const refreshUserInfoQuery = trpc.auth.refreshUserInfo.useQuery(undefined, {
    enabled: false,
  });

  const utils = trpc.useUtils();
  const updateMutation = trpc.spaces.update.useMutation({
    onSuccess: async () => {
      utils.spaces.get.invalidate({ id: spaceId! });
      setIsEditing(false);
      const refreshed = await refreshUserInfoQuery.refetch();
      if (refreshed.data) {
        const userData = refreshed.data as Parameters<typeof updateUserInfo>[0]['user'];
        dispatch(updateUserInfo({ user: userData }));
      }
    },
  });

  const handleStartEdit = useCallback(() => {
    if (space) {
      setEditName(space.name ?? '');
      setEditCode(space.code ?? '');
      setIsEditing(true);
    }
  }, [space]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(() => {
    if (!spaceId) return;
    updateMutation.mutate({
      id: spaceId,
      data: { name: editName || undefined, code: editCode || undefined },
    });
  }, [spaceId, editName, editCode, updateMutation]);

  const handleLogoConfirm = useCallback(
    (url: string) => {
      if (!spaceId) return;
      updateMutation.mutate({
        id: spaceId,
        data: { logoUrl: url },
      });
    },
    [spaceId, updateMutation]
  );

  if (!spaceId) {
    return (
      <div className="flex flex-1 flex-col overflow-y-auto p-6">
        <Card className="mx-auto w-full max-w-3xl">
          <Card.Content className="p-6">
            <p className="text-muted-foreground text-sm">暂无空间，无法进行设置</p>
          </Card.Content>
        </Card>
      </div>
    );
  }

  if (isLoading || !space) {
    return (
      <div className="flex flex-1 flex-col overflow-y-auto p-6">
        <Card className="mx-auto w-full max-w-3xl">
          <Card.Content className="p-6">
            <p className="text-muted-foreground text-sm">加载中…</p>
          </Card.Content>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-y-auto p-4 md:p-6">
      <Card className="mx-auto w-full max-w-3xl">
        <Card.Content className="flex flex-col gap-0 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 pb-2">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold">空间设置</h3>
              <Description className="text-sm">查看与编辑当前空间信息</Description>
            </div>
            {isEditing ? (
              <div className="flex shrink-0 items-center gap-2">
                <Button variant="ghost" size="sm" onPress={handleCancel}>
                  取消
                </Button>
                <Button
                  isDisabled={updateMutation.isPending}
                  size="sm"
                  variant="primary"
                  onPress={handleSave}
                >
                  保存
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="primary" onPress={handleStartEdit}>
                编辑
              </Button>
            )}
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-4 py-4">
            <Label className="text-sm font-medium text-foreground">空间 logo</Label>
            <div className="flex items-center gap-3">
              <Avatar className="size-14 shrink-0" variant="soft">
                {space.logoUrl ? (
                  <Avatar.Image src={space.logoUrl} alt={space.name ?? ''} />
                ) : null}
                <Avatar.Fallback className="text-base font-semibold">
                  {getSpaceInitial(space.name)}
                </Avatar.Fallback>
              </Avatar>
              <Button variant="ghost" size="sm" onPress={() => setLogoUploadOpen(true)}>
                上传
              </Button>
              <AvatarUploadDialog
                open={logoUploadOpen}
                onOpenChange={setLogoUploadOpen}
                title="上传 LOGO"
                onConfirm={handleLogoConfirm}
              />
            </div>
          </div>
          <Separator />

          <div className="flex flex-col gap-2 py-4 sm:flex-row sm:items-center sm:justify-between">
            <Label className="text-sm font-medium text-foreground">空间名称</Label>
            {isEditing ? (
              <TextField className="w-full max-w-[280px] sm:w-[280px]">
                <Input
                  placeholder="请输入空间名称"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </TextField>
            ) : (
              <span className="text-muted-foreground text-sm">{space.name ?? '—'}</span>
            )}
          </div>
          <Separator />

          <div className="flex flex-col gap-2 py-4 sm:flex-row sm:items-center sm:justify-between">
            <Label className="text-sm font-medium text-foreground">空间代码</Label>
            {isEditing ? (
              <TextField className="w-full max-w-[280px] sm:w-[280px]">
                <Input
                  placeholder="请输入空间代码"
                  value={editCode}
                  onChange={(e) => setEditCode(e.target.value)}
                />
              </TextField>
            ) : (
              <span className="text-muted-foreground text-sm">
                {space.code && space.code.trim() ? space.code : '未设置'}
              </span>
            )}
          </div>
          <Separator />

          <div className="flex flex-col gap-1 py-4 sm:flex-row sm:items-center sm:justify-between">
            <Label className="text-sm font-medium text-foreground">是否认证</Label>
            <span className="text-muted-foreground text-sm">
              {space.isVerified ? '已认证' : '未认证'}
            </span>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
}
